import {
  Camera as CapacitorCamera,
  CameraResultType,
  CameraSource,
} from "@capacitor/camera"
import { Camera, ImagePlus } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"

interface ImagePickerProps {
  onImageSelected: (file: File) => void
  preview?: string | null
  className?: string
  showPreview?: boolean
  previewSize?: "sm" | "md" | "lg"
  variant?: "button" | "avatar"
  name?: string // For form integration
}

export function ImagePicker({
  onImageSelected,
  preview,
  className = "",
  showPreview = true,
  previewSize = "md",
  variant = "button",
  name,
}: ImagePickerProps) {
  const galleryInputRef = React.useRef<HTMLInputElement>(null)
  const formInputRef = React.useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      processFile(file)
    }
    e.target.value = ""
  }

  const processFile = (file: File) => {
    onImageSelected(file)
    if (name && formInputRef.current) {
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      formInputRef.current.files = dataTransfer.files
    }
  }

  const openGallery = () => {
    galleryInputRef.current?.click()
  }

  const openCamera = async () => {
    try {
      const image = await CapacitorCamera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        correctOrientation: true,
      })

      if (image.dataUrl) {
        // Convert data URL to File
        const response = await fetch(image.dataUrl)
        const blob = await response.blob()
        const file = new File([blob], `photo_${Date.now()}.jpg`, {
          type: "image/jpeg",
        })
        processFile(file)
      }
    } catch (error) {
      // User cancelled or error occurred
      console.log("Camera cancelled or error:", error)
    }
  }

  const sizeClasses = {
    sm: "w-14 h-14",
    md: "w-24 h-24",
    lg: "w-32 h-32",
  }

  if (variant === "avatar") {
    return (
      <div className={`flex flex-col items-center gap-4 ${className}`}>
        {showPreview && (
          <div
            className={`flex items-center justify-center ${sizeClasses[previewSize]} bg-muted overflow-hidden rounded-full`}
          >
            {preview && (
              <img
                src={preview}
                alt="Preview"
                className="h-full w-full object-cover"
              />
            )}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            type="button"
            variant="default"
            size="sm"
            className="rounded-full"
            onClick={openGallery}
          >
            <ImagePlus className="mr-1.5 h-4 w-4" />
            Subir foto
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => {
              void openCamera()
            }}
          >
            <Camera className="mr-1.5 h-4 w-4" />
            Cámara
          </Button>
        </div>

        {/* Gallery input - opens file picker */}
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
        {/* Form input for submission */}
        {name && (
          <input
            ref={formInputRef}
            type="file"
            name={name}
            accept="image/*"
            className="hidden"
          />
        )}
      </div>
    )
  }

  // Default button variant
  return (
    <div className={`flex items-center gap-4 ${className}`}>
      {showPreview && preview && (
        <div
          className={`${sizeClasses[previewSize]} overflow-hidden rounded-full border border-neutral-200`}
        >
          <img
            src={preview}
            alt="Preview"
            className="h-full w-full object-cover"
          />
        </div>
      )}

      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={openGallery}>
          <ImagePlus className="mr-1.5 h-4 w-4" />
          Subir foto
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            void openCamera()
          }}
        >
          <Camera className="mr-1.5 h-4 w-4" />
          Cámara
        </Button>
      </div>

      {/* Gallery input - opens file picker */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
      {/* Form input for submission */}
      {name && (
        <input
          ref={formInputRef}
          type="file"
          name={name}
          accept="image/*"
          className="hidden"
        />
      )}
    </div>
  )
}
